import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, FolderIcon } from '@heroicons/react/24/outline';
import { laufwerkOrdner, type LaufwerkOrdner } from '../../api/client';
import { zahl } from '../../utils/laufwerk';

/**
 * Aufklappbarer Ordnerbaum mit Häkchen. Die Ebenen werden erst beim Aufklappen geladen
 * (GET /laufwerk/ordner?unter=). Ausgewählt sind Präfixe, die auf «/» enden; "" = ganzes Laufwerk.
 * Ist ein übergeordneter Ordner gewählt, sind die darunter liegenden mit abgehakt und gesperrt.
 */

type Knoten = { laedt: boolean; fehler?: string; ordner?: LaufwerkOrdner[] };

const alsGeerbt = (pfad: string, ausgewaehlt: string[]) => ausgewaehlt.some((p) => p !== pfad && pfad.startsWith(p));

function Haken({ pfad, ausgewaehlt, onToggle, label, zusatz }: {
  pfad: string; ausgewaehlt: string[]; onToggle: (pfad: string) => void; label: string; zusatz?: string;
}) {
  const geerbt = alsGeerbt(pfad, ausgewaehlt);
  const an = geerbt || ausgewaehlt.includes(pfad);
  return (
    <label className={`flex min-w-0 flex-1 items-center gap-2 py-1.5 text-sm ${geerbt ? 'cursor-default text-ps-dark-400' : 'cursor-pointer text-ps-dark-800'}`}
      title={geerbt ? 'Durch einen übergeordneten Ordner bereits enthalten' : undefined}>
      <input type="checkbox" checked={an} disabled={geerbt} onChange={() => onToggle(pfad)}
        className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
      <FolderIcon className="h-4 w-4 shrink-0 text-ps-dark-400" />
      <span className="min-w-0 break-all">{label}</span>
      {zusatz && <span className="numerical shrink-0 text-[11px] text-ps-dark-400">{zusatz}</span>}
    </label>
  );
}

function Ebene({ pfad, knoten, offen, ladeUnter, ausgewaehlt, onToggle }: {
  pfad: string;
  knoten: Record<string, Knoten>;
  offen: Record<string, boolean>;
  ladeUnter: (pfad: string) => void;
  ausgewaehlt: string[];
  onToggle: (pfad: string) => void;
}) {
  const k = knoten[pfad];
  if (!k || k.laedt) return <div className="py-1.5 pl-6 text-xs text-ps-dark-400">Lade …</div>;
  if (k.fehler) return <div className="py-1.5 pl-6 text-xs text-rose-700">{k.fehler}</div>;
  if (!k.ordner || k.ordner.length === 0) return <div className="py-1.5 pl-6 text-xs text-ps-dark-400">Keine Unterordner.</div>;
  return (
    <ul>
      {k.ordner.map((o) => {
        const auf = !!offen[o.pfad];
        return (
          <li key={o.pfad}>
            <div className="flex items-center">
              <button type="button" onClick={() => ladeUnter(o.pfad)} aria-label={auf ? `${o.name} zuklappen` : `${o.name} aufklappen`} aria-expanded={auf}
                className="flex h-8 w-6 shrink-0 items-center justify-center text-ps-dark-400 hover:text-ps-dark-700">
                {auf ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
              </button>
              <Haken pfad={o.pfad} ausgewaehlt={ausgewaehlt} onToggle={onToggle} label={o.name} zusatz={`${zahl(o.dateien)} ${o.dateien === 1 ? 'Datei' : 'Dateien'}`} />
            </div>
            {auf && (
              <div className="ml-3 border-l border-ps-dark-100 pl-2">
                <Ebene pfad={o.pfad} knoten={knoten} offen={offen} ladeUnter={ladeUnter} ausgewaehlt={ausgewaehlt} onToggle={onToggle} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function OrdnerBaum({ ausgewaehlt, onToggle }: { ausgewaehlt: string[]; onToggle: (pfad: string) => void }) {
  const [knoten, setKnoten] = useState<Record<string, Knoten>>({});
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  const [wurzelOffen, setWurzelOffen] = useState(false);

  const laden = (pfad: string) => {
    setKnoten((k) => ({ ...k, [pfad]: { laedt: true } }));
    laufwerkOrdner(pfad)
      .then((r) => setKnoten((k) => ({ ...k, [pfad]: { laedt: false, ordner: r.ordner } })))
      .catch((e: any) => setKnoten((k) => ({ ...k, [pfad]: { laedt: false, fehler: e?.message ?? String(e) } })));
  };

  const ladeUnter = (pfad: string) => {
    const auf = !offen[pfad];
    setOffen((o) => ({ ...o, [pfad]: auf }));
    if (auf && !knoten[pfad]) laden(pfad);
  };

  const wurzel = () => {
    const auf = !wurzelOffen;
    setWurzelOffen(auf);
    if (auf && !knoten['']) laden('');
  };

  return (
    <div className="rounded-lg border border-ps-dark-200 bg-white px-2 py-1">
      <div className="flex items-center">
        <button type="button" onClick={wurzel} aria-label={wurzelOffen ? 'Ordner zuklappen' : 'Ordner aufklappen'} aria-expanded={wurzelOffen}
          className="flex h-8 w-6 shrink-0 items-center justify-center text-ps-dark-400 hover:text-ps-dark-700">
          {wurzelOffen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
        </button>
        <Haken pfad="" ausgewaehlt={ausgewaehlt} onToggle={onToggle} label="Ganzes Laufwerk" />
      </div>
      {wurzelOffen && (
        <div className="ml-3 max-h-80 overflow-y-auto border-l border-ps-dark-100 pl-2">
          <Ebene pfad="" knoten={knoten} offen={offen} ladeUnter={ladeUnter} ausgewaehlt={ausgewaehlt} onToggle={onToggle} />
        </div>
      )}
    </div>
  );
}
